import * as api from '../api';
import { PURGE } from 'redux-persist';

const GET_FLEET_RESERVATIONS = 'reservations/GET_FLEET_RESERVATIONS';
const GET_DAILY_FLEET_RESERVATIONS = 'reservations/GET_DAILY_FLEET_RESERVATIONS';
const GET_MONTHLY_FLEET_RESERVATIONS = 'reservations/GET_MONTHLY_FLEET_RESERVATIONS';
const END_FLEET_RESERVATION = 'reservations/END_FLEET_RESERVATION';
const REFUND_FLEET_RESERVATION = 'reservations/REFUND_FLEET_RESERVATION';
const PARTIALLY_REFUND_FLEET_RESERVATION = 'reservations/PARTIALLY_REFUND_FLEET_RESERVATION';
const ACTIVATE_FLEET_RESERVATION = 'reservations/ACTIVATE_FLEET_RESERVATION';
const REASSIGN_VEHICLE_TO_RESERVATION = 'reservations/REASSIGN_FLEET_RESERVATION';
const UNASSIGN_FLEET_RESERVATION = 'reservations/UNASSIGN_FLEET_RESERVATION';
const CREATE_MANUAL_RESERVATION = 'reservations/CREATE_MANUAL_RESERVATION';
const UPDATE_RESERVATION = 'reservations/UPDATE_RESERVATION';
// Keep old constant for backward compatibility
const UPDATE_MANUAL_RESERVATION = UPDATE_RESERVATION;

// Helper function to standardize reservation data and ensure IDs are always strings
const standardizeReservationData = (reservations) => {
  if (!Array.isArray(reservations)) return reservations;
  
  return reservations.map(reservation => ({
    ...reservation,
    id: String(reservation.id) // Ensure ID is always a string
  }));
};

// Change initialState to an object to hold separate reservations arrays
const initialState = {
  fleetReservations: [],
  dailyReservations: [],
  monthlyReservations: [],
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case GET_FLEET_RESERVATIONS:
      return { 
        ...state, 
        fleetReservations: standardizeReservationData(action.data)
      };
    case GET_DAILY_FLEET_RESERVATIONS:
      return { 
        ...state, 
        dailyReservations: standardizeReservationData(action.data)
      };
    case GET_MONTHLY_FLEET_RESERVATIONS:
      return { 
        ...state, 
        monthlyReservations: standardizeReservationData(action.data)
      };
    case END_FLEET_RESERVATION:
      return {
        ...state,
        fleetReservations: Array.isArray(state.fleetReservations) 
          ? state.fleetReservations.filter((r) => String(r.id) !== String(action.data)) 
          : state.fleetReservations,
        dailyReservations: Array.isArray(state.dailyReservations) 
          ? state.dailyReservations.filter((r) => String(r.id) !== String(action.data)) 
          : state.dailyReservations,
        monthlyReservations: (() => {
          // Handle different possible structures of monthlyReservations
          if (Array.isArray(state.monthlyReservations)) {
            // If it's an array, filter it directly
            return state.monthlyReservations.filter((r) => String(r.id) !== String(action.data));
          } else if (state.monthlyReservations && typeof state.monthlyReservations === 'object') {
            // If it's an object, check if it has a reservations property that's an array
            if (Array.isArray(state.monthlyReservations.reservations)) {
              return {
                ...state.monthlyReservations,
                reservations: state.monthlyReservations.reservations.filter(r => 
                  String(r.id) !== String(action.data)
                )
              };
            }
          }
          // In all other cases, just return the original state
          return state.monthlyReservations;
        })(),
      };
    case ACTIVATE_FLEET_RESERVATION:
      return state;
    case REFUND_FLEET_RESERVATION:
    case REASSIGN_VEHICLE_TO_RESERVATION:
    case UNASSIGN_FLEET_RESERVATION:
    case PARTIALLY_REFUND_FLEET_RESERVATION:
      return {
        ...state,
        fleetReservations: Array.isArray(state.fleetReservations)
          ? state.fleetReservations.map((r) => String(r.id) === String(action.data.id) ? { ...r, refunded: true } : r)
          : state.fleetReservations,
        dailyReservations: Array.isArray(state.dailyReservations)
          ? state.dailyReservations.map((r) => String(r.id) === String(action.data.id) ? { ...r, refunded: true } : r)
          : state.dailyReservations,
        monthlyReservations: Array.isArray(state.monthlyReservations)
          ? state.monthlyReservations.map((r) => String(r.id) === String(action.data.id) ? { ...r, refunded: true } : r)
          : state.monthlyReservations.reservations
            ? {
                ...state.monthlyReservations,
                reservations: state.monthlyReservations.reservations.map(r => 
                  String(r.id) === String(action.data.id) ? { ...r, refunded: true } : r
                )
              }
            : state.monthlyReservations,
      };
    case CREATE_MANUAL_RESERVATION:
      // Add new reservation to all reservation arrays
      return {
        ...state,
        fleetReservations: Array.isArray(state.fleetReservations)
          ? [...state.fleetReservations, standardizeReservationData([action.data])[0]]
          : [standardizeReservationData([action.data])[0]],
        // Note: dailyReservations and monthlyReservations will be refreshed on next fetch
      };
    case UPDATE_RESERVATION:
    case UPDATE_MANUAL_RESERVATION: // Keep for backward compatibility
      // Update reservation in all arrays
      return {
        ...state,
        fleetReservations: Array.isArray(state.fleetReservations)
          ? state.fleetReservations.map((r) => 
              String(r.id) === String(action.data.id) ? { ...r, ...action.data } : r
            )
          : state.fleetReservations,
        dailyReservations: Array.isArray(state.dailyReservations)
          ? state.dailyReservations.map((r) => 
              String(r.id) === String(action.data.id) ? { ...r, ...action.data } : r
            )
          : state.dailyReservations,
        monthlyReservations: Array.isArray(state.monthlyReservations)
          ? state.monthlyReservations.map((r) => 
              String(r.id) === String(action.data.id) ? { ...r, ...action.data } : r
            )
          : state.monthlyReservations.reservations
            ? {
                ...state.monthlyReservations,
                reservations: state.monthlyReservations.reservations.map(r => 
                  String(r.id) === String(action.data.id) ? { ...r, ...action.data } : r
                )
              }
            : state.monthlyReservations,
      };
    case PURGE:
      return initialState;
    default:
      return state;
  }
}

export const activateFleetReservation = (jwt, reservation_id, params) => {
  return (dispatch) => {
    return api
      .activateFleetReservation(jwt, reservation_id, params)
      .then((data) => dispatch({ type: ACTIVATE_FLEET_RESERVATION, data: reservation_id }));
  };
};

export const getFleetReservations = (jwt, fleet_id) => {
    return (dispatch) => {
      return api
        .getFleetReservations(jwt, fleet_id)
        .then((data) => dispatch({ type: GET_FLEET_RESERVATIONS, data: data }));
    };
  };

export const getDayFleetReservations = (jwt, fleet_id, day) => {
  return (dispatch) => {
    return api
      .getDayFleetReservations(jwt, fleet_id, day)
      .then((data) => {
        dispatch({ type: GET_DAILY_FLEET_RESERVATIONS, data: data });
      });
  };
};


export const getMonthlyFleetReservations = (jwt, fleet_id, month, year) => {
  return (dispatch) => {
    return api
      .getFleetReservations(jwt, fleet_id, month, year)
      .then((data) => {
        // Extract the reservations array if data is wrapped in an object
        let reservationsArray = data;
        
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          if (data.reservations && Array.isArray(data.reservations)) {
            reservationsArray = data.reservations;
          }
        }
        
        dispatch({ type: GET_MONTHLY_FLEET_RESERVATIONS, data: reservationsArray });
        return reservationsArray;
      });
  };
};

export const endFleetReservation = (jwt, reservation_id, params) => {
  return (dispatch) => {
    return api
      .endFleetReservation(jwt, reservation_id, params)
      .then((result) => {
        dispatch({ type: END_FLEET_RESERVATION, data: reservation_id });
        return result;
      });
  };
};

export const partiallyRefundFleetReservation = (jwt, reservationId, params) => {
  return async (dispatch) => {
    try {
      const data = await api.refundPartialReservation(jwt, reservationId, params);
      dispatch({ type: PARTIALLY_REFUND_FLEET_RESERVATION, data: params });
      return data;
    } catch (error) {
      console.error('Error in refunding:', error);
      throw error;
    }
  };
};

export const refundFleetReservation = (jwt, reservation_id, params) => {
  return async (dispatch) => {
    try {
      const data = await api.refundReservation(jwt, reservation_id, params);
      dispatch({ type: REFUND_FLEET_RESERVATION, data: { id: reservation_id } });
      return data;
    } catch (error) {
      console.error('Error in refunding:', error);
      throw error;
    }
  };
};

export const unassignFleetReservation = (jwt, reservation_id) => {
  return async (dispatch) => {
    try {
      await api.unassignFleetReservation(jwt, reservation_id);
      dispatch({ type: UNASSIGN_FLEET_RESERVATION, data: { id: reservation_id } });
    } catch (error) {
      console.error('Error unassigning reservation:', error);
      throw error;
    }
  };
};

export const reassignVehicle = (jwt, reservation_id, params) => {
  return async (dispatch) => {
    try {
      const data = await api.reassignVehicleToReservation(jwt, reservation_id, params);
      dispatch({ type: REASSIGN_VEHICLE_TO_RESERVATION, data: { id: reservation_id } });
      return data;
    } catch (error) {
      console.error('Error in refunding:', error);
      throw error;
    }
  };
};

// Manual Reservation Management Actions

/**
 * Create a new manual reservation
 * @param {string} jwt - Authentication token
 * @param {string} fleetId - Fleet ID
 * @param {object} reservationData - Reservation details
 * @returns {Promise} - Resolves with created reservation data
 */
export const createManualReservation = (jwt, fleetId, reservationData) => {
  return async (dispatch) => {
    try {
      const data = await api.createManualReservation(jwt, fleetId, reservationData);
      dispatch({ type: CREATE_MANUAL_RESERVATION, data });
      return data;
    } catch (error) {
      console.error('Error creating manual reservation:', error);
      throw error;
    }
  };
};

/**
 * Update an existing reservation (works for ANY reservation source)
 * @param {string} jwt - Authentication token
 * @param {string} reservationId - Reservation ID to update
 * @param {object} updateData - Fields to update
 * @returns {Promise} - Resolves with updated reservation data
 */
export const updateReservation = (jwt, reservationId, updateData) => {
  return async (dispatch) => {
    try {
      const data = await api.updateReservation(jwt, reservationId, updateData);
      dispatch({ type: UPDATE_RESERVATION, data });
      return data;
    } catch (error) {
      console.error('Error updating reservation:', error);
      throw error;
    }
  };
};

// Keep old function name for backward compatibility
export const updateManualReservation = updateReservation;





